/**
 * @file AddToOrderModal.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-23
 * @modified 2026-10-23
 * @description "Add to another order…" — a split order's tonnage added to the
 *              customer's other order delivered that day, so the customer gets
 *              one invoice with one line (Cam, 23 Oct 2026 — SO-D10421A's
 *              1.94 T of Harvester 18, split off R J Stitch's SO-D10421, added
 *              to GCB Davis's SO-D10545 Gold Label 18: 5.94 T + 1.94 T =
 *              7.88 T). Also "Added tonnage…": what an order gave or takes,
 *              with Take it back out. Rules: utils/tonnageAdditions.js (the
 *              server's copy is generated from it); server:
 *              services/orderLineAdditionService.js.
 *
 *              Nothing else changes: the split order stays the split, on its
 *              PO, with its supplied quantity; the other order's own quantity
 *              and supplied quantity stay as they are. The tonnage is a
 *              separate figure its invoice adds to the line.
 *
 *              Used from Search Customer Orders (row menu, right-click, the
 *              pill), the order window (footer, pill).
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { API_URL } from '../config';
import Modal from './Modal';
import Dropdown from './Dropdown';
import Textarea from './Textarea';
import LoadingSpinner from './LoadingSpinner';
import { useSlideOutButton } from './SlideOutButton';
import { ukDay } from '../utils/transportLock';
import {
  additionWords, convertToTarget, defaultTargetLine, kgPerUnit, quantityInUnitText, ownInvoiceQuantity
} from '../utils/tonnageAdditions';
import './TonnageAddition.css';

const lineLabel = (l) => {
  const qty = quantityInUnitText(ownInvoiceQuantity(l), kgPerUnit(l));
  return `${l.product_code ? `${l.product_code} · ` : ''}${l.product_name || 'Line'} — ${qty}${l.supplied_quantity === null ? ' ordered' : ' supplied'}`;
};

function AdditionCard({ a, side, saving, onRemove }) {
  const gave = side === 'gave';
  return (
    <div className="tonnage-card">
      <div className="tonnage-card-head">
        <span className="material-symbols-rounded">{gave ? 'call_merge' : 'add_circle'}</span>
        <strong>
          {gave
            ? `${a.source_order_number}'s ${a.source_unit_text} (${a.source_product || 'its product'}) is invoiced on ${a.target_order_number}`
            : `${a.target_order_number}'s ${a.target_product || 'line'} includes ${a.unit_text || '—'} from ${a.source_order_number}`}
        </strong>
      </div>
      <p className="tonnage-card-words">
        {a.target_order_number}'s {a.target_product || 'line'}: {a.target_own_text} + {a.unit_text || '—'} = {a.target_total_text}
        {a.target_invoice_no ? ` — invoiced on ${a.target_invoice_no}.` : ' — not invoiced yet.'}
        {a.parent_order_number ? ` ${a.source_order_number} stays the split of ${a.parent_order_number}${a.parent_company_name ? ` (${a.parent_company_name})` : ''}${a.source_po_number ? `, on ${a.source_po_number}` : ''}.` : ''}
      </p>
      {a.problem && <p className="tonnage-problem">{a.problem}</p>}
      <p className="tonnage-meta">
        Added{a.created_by_name ? ` by ${a.created_by_name}` : ''}{a.created_at ? ` on ${ukDay(String(a.created_at).slice(0, 10))}` : ''}
        {a.reason ? ` — ${a.reason}` : ''}
      </p>
      <div>
        <button type="button" data-write-action className={`btn-slideout${a.remove_block ? ' disabled' : ''}`}
          disabled={saving || !!a.remove_block}
          title={a.remove_block || `Take ${a.source_order_number}'s tonnage back out — it is invoiced on its own again`}
          onClick={() => onRemove(a)}>
          <div className="icon-wrapper"><span className="material-symbols-rounded">call_split</span></div>
          <span className="btn-label">Take it back out</span>
        </button>
      </div>
    </div>
  );
}

function AddToOrderBody({ data, targetId, setTargetId, lineChoice, setLineChoice, reason, setReason, saving, error, onRemove, plan }) {
  useSlideOutButton([saving, data, targetId]);
  const { order, added_to: addedTo = [], includes = [], candidates = [], block } = data;
  const viewing = addedTo.length > 0 || includes.length > 0;
  const target = candidates.find(c => c.id === targetId) || null;

  return (
    <div className="tonnage-body">
      <p className="tonnage-intro">
        {order.order_number} · {order.company_name}{order.account_no ? ` (${order.account_no})` : ''} · delivered {ukDay(order.delivery_date) || '—'}.
        {order.parent ? ` Split from ${order.parent.order_number}${order.parent.company_name ? ` (${order.parent.company_name})` : ''}.` : ''}
        {' '}Adding its tonnage to the customer's other order that day invoices both as one line on that order's invoice,
        {' '}with that order's product, words and price. Nothing else changes: {order.order_number} stays the split, on its PO,
        {' '}with its supplied quantity; the other order's own quantity and supplied quantity stay as they are.
      </p>

      {addedTo.map(a => <AdditionCard key={a.id} a={a} side="gave" saving={saving} onRemove={onRemove} />)}
      {includes.map(a => <AdditionCard key={a.id} a={a} side="takes" saving={saving} onRemove={onRemove} />)}

      {!viewing && block && (
        <p className="tonnage-block">
          <span className="material-symbols-rounded">info</span>
          {block}
        </p>
      )}

      {!viewing && candidates.length > 0 && (
        <div className="tonnage-field">
          <label>Add it to <span className="required-field">*</span></label>
          <div className="tonnage-candidates">
            {candidates.map(c => (
              <button type="button" key={c.id}
                className={`tonnage-candidate${c.id === targetId ? ' is-chosen' : ''}${c.block ? ' is-blocked' : ''}`}
                disabled={!!c.block || !!block || saving}
                title={c.block || `Add the tonnage to ${c.order_number}`}
                onClick={() => setTargetId(c.id)}>
                <span className="material-symbols-rounded">{c.id === targetId ? 'radio_button_checked' : 'radio_button_unchecked'}</span>
                <span className="tonnage-candidate-text">
                  <strong>{c.order_number}</strong>
                  <span>{c.lines.map(l => `${l.product_name || l.product_code} ${quantityInUnitText(ownInvoiceQuantity(l), l.kg)}`).join(' · ') || 'No lines'}</span>
                  {c.block && <span className="tonnage-candidate-why">{c.block}</span>}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}

      {!viewing && target && (
        <div className="tonnage-field">
          <label>Onto which line</label>
          {order.lines.map(sl => (
            <div className="tonnage-line-choice" key={sl.id}>
              <span className="tonnage-line-from">{sl.product_name || sl.product_code} ({quantityInUnitText(sl.supplied_quantity, sl.kg)})</span>
              <span className="material-symbols-rounded">arrow_forward</span>
              <Dropdown
                value={String(lineChoice[sl.id] ?? defaultTargetLine(sl, target.lines) ?? '')}
                onChange={(v) => setLineChoice(m => ({ ...m, [sl.id]: Number(v) }))}
                options={target.lines.map(l => ({ value: String(l.id), label: lineLabel(l) }))}
                disabled={saving || target.lines.length < 2}
              />
            </div>
          ))}
        </div>
      )}

      {!viewing && plan && (
        <div className={`tonnage-preview${plan.problem ? ' is-error' : ''}`}>
          <span className="material-symbols-rounded">{plan.problem ? 'error' : 'receipt_long'}</span>
          <span>{plan.problem || plan.words}</span>
        </div>
      )}

      {!viewing && target && (
        <div className="tonnage-field">
          <label>Note (optional, kept in the history)</label>
          <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} maxLength={1000} disabled={saving}
            placeholder="e.g. both 18% protein — one invoice for the delivery" />
        </div>
      )}

      {error && <p className="tonnage-error">{error}</p>}
    </div>
  );
}

/**
 * @param {number}   orderId  The order (the split order, or the order taking its tonnage).
 * @param {function} onClose  Always called when the window closes.
 * @param {function} onDone   (message) after an add or a take-out.
 */
function AddToOrderModal({ orderId, onClose, onDone }) {
  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [targetId, setTargetId] = useState(null);
  const [lineChoice, setLineChoice] = useState({});
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/orders/customer-orders/${orderId}/tonnage-additions`, { credentials: 'include' });
      const body = await res.json().catch(() => ({}));
      if (!res.ok || !body.success) throw new Error(body.error || `Could not load (${res.status})`);
      if (body.ready === false) throw new Error(body.reason || 'Not ready');
      setData(body);
      const ok = (body.candidates || []).filter(c => !c.block);
      if (ok.length === 1) setTargetId(ok[0].id);
    } catch (err) {
      setLoadError(err.message);
    }
  }, [orderId]);

  useEffect(() => { load(); }, [load]);

  const viewing = !!data && ((data.added_to || []).length > 0 || (data.includes || []).length > 0);
  const target = data ? (data.candidates || []).find(c => c.id === targetId) || null : null;

  // What will happen, in words — worked out with the server's own rules.
  const plan = useMemo(() => {
    if (!data || viewing || !target) return null;
    const lines = data.order.lines || [];
    const words = [];
    for (const sl of lines) {
      const tId = lineChoice[sl.id] ?? defaultTargetLine(sl, target.lines);
      const tl = target.lines.find(l => l.id === Number(tId));
      if (!tl) return { problem: `Choose a line of ${target.order_number} for ${sl.product_name || sl.product_code}`, lines: [] };
      const conv = convertToTarget(sl.supplied_quantity, sl, tl);
      if (!conv.ok) return { problem: conv.reason, lines: [] };
      words.push(additionWords({
        source: data.order, sourceLine: sl, target, targetLine: tl, quantity: conv.quantity,
        parent: data.order.parent, poNumber: sl.purchase_order_number || null
      }));
    }
    return { problem: null, words: words.join(' '), lines: lines.map(sl => ({ source_line_id: sl.id, target_line_id: Number(lineChoice[sl.id] ?? defaultTargetLine(sl, target.lines)) })) };
  }, [data, viewing, target, lineChoice]);

  const post = async (path, body) => {
    setSaving(true);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/api/orders/customer-orders/${orderId}/${path}`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body)
      });
      const out = await res.json().catch(() => ({}));
      if (!res.ok || !out.success) throw new Error(out.error || `Could not save (${res.status})`);
      onDone && onDone(out.message);
      onClose();
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  };

  const add = () => {
    if (!plan || plan.problem || !target) return;
    post('tonnage-additions', { target_order_id: target.id, lines: plan.lines, reason: reason.trim() || undefined });
  };
  const remove = (a) => post('tonnage-additions/remove', { addition_ids: [a.id] });

  const addBlock = !data ? 'Loading…' : viewing ? null
    : (data.block || (!target ? 'Choose the order to add it to' : (plan?.problem || null)));

  const title = data
    ? (viewing ? `Added tonnage — ${data.order.order_number}` : `Add to another order — ${data.order.order_number}`)
    : 'Add to another order';

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={title}
      size="lg"
      className="tonnage-addition"
      footer={(
        <div className="buttons slideout">
          <div>
            <button data-read-safe className="btn-slideout" onClick={onClose} disabled={saving}>
              <div className="icon-wrapper"><span className="material-symbols-rounded">close</span></div>
              <span className="btn-label">Close</span>
            </button>
          </div>
          {!viewing && (
            <div>
              <button data-write-action className={`btn-slideout${addBlock ? ' disabled' : ''}`} onClick={add} disabled={saving || !!addBlock}
                title={addBlock || 'Add the tonnage — it is invoiced on that order\'s line'}>
                <div className="icon-wrapper"><span className="material-symbols-rounded">call_merge</span></div>
                <span className="btn-label">{saving ? 'Adding…' : 'Add tonnage'}</span>
              </button>
            </div>
          )}
        </div>
      )}
    >
      {loadError ? (
        <p className="tonnage-error">{loadError}</p>
      ) : !data ? (
        <LoadingSpinner />
      ) : (
        <AddToOrderBody
          data={data}
          targetId={targetId}
          setTargetId={setTargetId}
          lineChoice={lineChoice}
          setLineChoice={setLineChoice}
          reason={reason}
          setReason={setReason}
          saving={saving}
          error={error}
          onRemove={remove}
          plan={plan}
        />
      )}
    </Modal>
  );
}

export default AddToOrderModal;
